import { useEffect, useRef, useState } from 'react';
import { Button, Icon, Panel, SectionCard, type BadgeTone } from '../../../components/ui';
import type { AcaoInsight, InsightDisparo } from '../insights';

/**
 * "O que fazer agora": a lista de insights acionaveis, em ordem de urgencia.
 *
 * Cada item e um `Panel` com o tom do insight, numero de ordem, titulo com o
 * numero que o justifica, uma frase de contexto e UM botao. A acao volta para
 * a pagina como dado (`AcaoInsight`): e ela que sabe navegar, retomar ou
 * preparar o formulario. O vazio e uma boa noticia escrita, nao um cartao em
 * branco.
 *
 * Clique duplo: resolver um item (ex.: "Retomar agora") o tira da lista e o
 * botao do PROXIMO sobe para o mesmo lugar. A lista inteira fica travada por
 * `TRAVA_MS` depois de cada clique, para o segundo clique nao disparar a
 * acao vizinha.
 */

const TRAVA_MS = 700;

const TOM_DO_PAINEL: Readonly<Record<BadgeTone, 'surface' | 'brand' | 'good' | 'warning' | 'critical' | 'accent'>> = {
  neutral: 'surface',
  brand: 'brand',
  accent: 'accent',
  info: 'surface',
  good: 'good',
  warning: 'warning',
  critical: 'critical',
  violet: 'surface',
};

const TOM_DO_ICONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink/5 text-ink-muted',
  brand: 'bg-brand/10 text-brand',
  accent: 'bg-accent/10 text-accent',
  info: 'bg-info/10 text-info',
  good: 'bg-status-good/10 text-status-good',
  warning: 'bg-status-warning/15 text-status-warning',
  critical: 'bg-status-critical/10 text-status-critical',
  violet: 'bg-series-7/10 text-series-7',
};

export function OQueFazerAgora({
  insights,
  onAcao,
  descricao,
  vazio,
  className = '',
}: {
  readonly insights: readonly InsightDisparo[];
  readonly onAcao: (acao: AcaoInsight) => void;
  readonly descricao: string;
  readonly vazio: string;
  readonly className?: string;
}): JSX.Element {
  const [travado, setTravado] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    },
    [],
  );
  const acionar = (acao: AcaoInsight): void => {
    if (travado) {
      return;
    }
    setTravado(true);
    timer.current = window.setTimeout(() => setTravado(false), TRAVA_MS);
    onAcao(acao);
  };
  return (
    <SectionCard id="o-que-fazer-agora" title="O que fazer agora" icon="clique" description={descricao} className={className}>
      {insights.length === 0 ? (
        <div className="flex flex-col gap-1 rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center">
          <p className="text-sm font-medium text-ink">Nada urgente por aqui.</p>
          <p className="text-sm text-ink-muted">{vazio}</p>
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {insights.map((insight, i) => {
            const acao = insight.acao;
            return (
              <li key={insight.id}>
                <Panel tone={TOM_DO_PAINEL[insight.tone]} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span aria-hidden="true" className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${TOM_DO_ICONE[insight.tone]}`}>
                      <Icon name={insight.icone} size="sm" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink">
                        <span className="mr-1.5 tabular-nums text-ink-subtle">{i + 1}.</span>
                        {insight.titulo}
                      </p>
                      <p className="mt-0.5 text-sm text-ink-muted">{insight.texto}</p>
                    </div>
                  </div>
                  {acao === null ? null : (
                    <Button variant="secondary" size="sm" iconRight="seta-dir" disabled={travado} onClick={() => acionar(acao)} className="shrink-0">
                      {acao.rotulo}
                    </Button>
                  )}
                </Panel>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}
